
import cartMixin from '~/mixins/cartMixin'
import commercialOperationPrice from '~/mixins/commercialOperationPrice'
import {
  describeColorTemperature,
  withoutColorTemperatureSpecifications,
} from '~/utils/colorTemperature'
import { openIntent } from '~/utils/openIntent'
import { writeProductCartContext } from '~/utils/productCartContextStorage'
import { joinTypeNames } from '~/utils/sharedBulbs'

const featuredSpecPriority = [142, 139, 8, 7, 26]

export default {
  name: 'VehicleHeadlightHeroCard',
  directives: { openIntent },
  mixins: [cartMixin, commercialOperationPrice],
  props: {
    product: { type: Object, required: true },
    heroSource: { type: String, required: true },
    vehicle: { type: Object, required: true },
    cartMetadata: { type: Object, default: null },
    sharedBulb: { type: Object, default: null },
    compareCount: { type: Number, default: 0 },
    compareSocket: { type: String, default: null },
    compareOpen: { type: Boolean, default: false },
    compareControls: { type: String, default: null },
  },
  data() {
    return {
      activeMedia: 0,
    }
  },
  computed: {
    operationPrice() {
      return this.operationPriceOf(this.product, { beforeTax: false })
    },
    compareHideLabel() {
      return this.$tc(
        'components.vehicleHeadlightHeroCard.hideLink',
        this.compareCount,
        { count: this.compareCount }
      )
    },
    compareShowLabel() {
      return this.compareSocket
        ? this.$tc(
            'components.vehicleHeadlightHeroCard.compareLinkWithSocket',
            this.compareCount,
            { count: this.compareCount, socket: this.compareSocket }
          )
        : this.$tc(
            'components.vehicleHeadlightHeroCard.compareLink',
            this.compareCount,
            { count: this.compareCount }
          )
    },
    isPackBadge() {
      return ['pack', 'only_pack', 'pinned_pack'].includes(this.heroSource)
    },
    isPinnedBadge() {
      return this.heroSource === 'pinned'
    },
    isSoleProductBadge() {
      return this.heroSource === 'only'
    },
    badgeClass() {
      if (this.isPackBadge || this.isPinnedBadge) {
        return 'tw-bg-[#ffa41c]/15 tw-text-[#ffa41c]'
      }
      if (this.isSoleProductBadge) return 'tw-bg-[#e9eef4]/15 tw-text-[#e9eef4]'
      return 'tw-bg-[#7ed321]/15 tw-text-[#7ed321]'
    },
    badgeIcon() {
      if (this.isPackBadge) return ['fas', 'box-full']
      if (this.isPinnedBadge) return ['fas', 'star']
      if (this.isSoleProductBadge) return ['fas', 'car']
      return ['fas', 'crown']
    },
    badgeLabel() {
      if (this.isPackBadge) {
        return this.$t('components.vehicleHeadlightHeroCard.recommendedPack')
      }
      if (this.isPinnedBadge) {
        return this.$t('components.vehicleHeadlightHeroCard.ourRecommendation')
      }
      if (this.isSoleProductBadge) {
        return this.$t(
          'components.vehicleHeadlightHeroCard.compatibleWithVehicle'
        )
      }
      return this.$t('components.vehicleHeadlightHeroCard.mostPopular')
    },
    sharedPartnerNames() {
      return joinTypeNames(this.sharedBulb?.partnerTypes)
    },
    productLink() {
      return this.localePath({
        name: 'produit-slug',
        params: { slug: this.product.slug },
      })
    },
    galleryImages() {
      const productImages = this.product.images || []
      const hasMultipleVariants =
        (this.product.selectable_variants || []).length > 1
      if (hasMultipleVariants && productImages.length) return productImages
      return [...productImages, ...(this.product.variant?.images || [])]
    },
    imageSrc() {
      const image = this.galleryImages[0]
      if (!image?.url) return null
      return this.$cropUrl(image, 480, 480, image.extension, 86)
    },
    mediaItems() {
      const seen = new Set()
      const images = [...this.galleryImages]
        .filter(
          img => img && img.url && !seen.has(img.url) && seen.add(img.url)
        )
        .slice(0, 4)
        .map(img => ({
          type: 'image',
          upload: img,
          url: img.url,
          extension: img.extension,
        }))
      const movie = (this.product.movies || [])[0]
      if (movie && movie.video) {
        images.splice(Math.min(1, images.length), 0, {
          type: 'video',
          url: movie.video.url,
          poster: movie.image
            ? this.$cropUrl(movie.image, 480, 480, movie.image.extension, 86)
            : null,
        })
      }
      return images.slice(0, 5)
    },
    hasDiscount() {
      return (
        this.product.variant.user_price_ttc < this.product.variant.price_ttc
      )
    },
    featuredSpecs() {
      const variant = this.product.variant || {}
      const bulbs = this.product.bulbs_quantity
      const perBulb = (value, unit) =>
        bulbs > 1
          ? `(${(value / bulbs).toLocaleString('fr-FR')} ${unit}${this.$t(
              'pages.produit.slug.perBulb'
            )})`
          : null
      const attributeSpecs = []
      const excludedSpecIds = []
      if (variant.luminosity_gain) {
        attributeSpecs.push({
          key: 'gain',
          name: this.$t('pages.produit.slug.luminosityGain'),
          value: this.$t('pages.produit.slug.upToLuminosityGain', {
            luminosityGain: variant.luminosity_gain,
          }),
          info: this.$t('pages.produit.slug.comparedToStandardHalogenBulbs'),
        })
        excludedSpecIds.push(142, 139)
      }
      if (variant.watts) {
        attributeSpecs.push({
          key: 'watts',
          name: this.$t('pages.produit.slug.totalPower'),
          value: `${variant.watts} W`,
          detail: perBulb(variant.watts, 'W'),
        })
        excludedSpecIds.push(8)
      }
      if (variant.lumens) {
        attributeSpecs.push({
          key: 'lumens',
          name: this.$t('pages.produit.slug.maxLuminousFlux'),
          value: `${variant.lumens.toLocaleString('fr-FR')} lm`,
          detail: perBulb(variant.lumens, 'lm'),
        })
        excludedSpecIds.push(7, 26)
      }
      if (variant.color_temperature) {
        attributeSpecs.push({
          key: 'color_temperature',
          name: this.$t('pages.produit.slug.colorTemperature'),
          value: describeColorTemperature(variant),
          kelvin: variant.color_temperature,
          colorHelp: true,
        })
      }
      const rank = spec => {
        const index = featuredSpecPriority.indexOf(spec.specification?.id)
        return index === -1 ? featuredSpecPriority.length : index
      }
      const specValues = withoutColorTemperatureSpecifications(
        variant.specification_values_featured,
        variant
      )
        .filter(sv => !excludedSpecIds.includes(sv.specification?.id))
        .sort((a, b) => rank(a) - rank(b))
        .map(sv => ({
          key: `spec-${sv.id}`,
          name: sv.specification?.name,
          value: sv.value,
        }))
      return [...attributeSpecs, ...specValues].slice(
        0,
        variant.color_temperature ? 4 : 3
      )
    },
    canAddToCart() {
      const variant = this.product.variant
      if (!variant) return false
      if (this.product.use_alternative_variants_layout) return false
      if ((this.product.selectable_variants || []).length >= 2) return false
      return (
        variant.quantity_available > 0 ||
        (variant.out_of_stock_status_id > 1 &&
          variant.out_of_stock_status_id < 4)
      )
    },
  },
  methods: {
    focusCompare() {
      const toggle = this.$refs.compareToggle
      const el = toggle && (toggle.$el || toggle)
      if (el && typeof el.focus === 'function') {
        el.focus({ preventScroll: true })
      }
    },
    async onAddToCart() {
      const added = await this.addToCart(
        this.product,
        this.product.variant,
        1,
        this.cartMetadata
      )
      if (!added) return
      this.$trackVehicleEvent(this.vehicle.id, 'add_to_cart_from_dashboard', {
        headlight_type_id: this.cartMetadata?.headlight_type_id,
        product_id: this.product.id,
        metadata: {
          hero_source: this.heroSource,
        },
      })
    },
    rememberCartContext() {
      writeProductCartContext(this.product.id, this.cartMetadata)
    },
    onProductClick() {
      this.rememberCartContext()
      this.trackOperationBadgeProductClick(this.product, this.operationPrice)
      this.$trackVehicleEvent(this.vehicle.id, 'product_click_from_vehicle', {
        headlight_type_id: this.cartMetadata?.headlight_type_id,
        product_id: this.product.id,
        metadata: {
          hero_source: this.heroSource,
        },
      })
    },
    getMediaVideoEl(index) {
      const ref = this.$refs['mediaVideo-' + index]
      return Array.isArray(ref) ? ref[0] : ref
    },
    onMediaScroll(event) {
      const strip = event.target
      const index = Math.round(strip.scrollLeft / strip.clientWidth)
      if (index === this.activeMedia) return
      const previousItem = this.mediaItems[this.activeMedia]
      if (previousItem && previousItem.type === 'video') {
        const video = this.getMediaVideoEl(this.activeMedia)
        if (video) video.pause()
      }
      this.activeMedia = index
    },
    goToMedia(index, behavior = 'smooth') {
      const strip = this.$refs.mediaStrip
      if (!strip) return
      strip.scrollTo({ left: index * strip.clientWidth, behavior })
    },
    hoverMedia(index) {
      this.goToMedia(index, 'auto')
      const item = this.mediaItems[index]
      if (item && item.type === 'video') {
        this.$nextTick(() => {
          const video = this.getMediaVideoEl(index)
          if (video) video.play().catch(() => {})
        })
      }
    },
  },
}
